// ===============================================================
// ProBank — Transfer to Bank
//
// A clean, professional bank transfer experience:
// real bank list, live account verification, recent recipients,
// confirm screen, TRANSACTION PIN authorization, and a proper
// bank-style receipt.
//
// ORDER OF CHECKS (like real banks):
//   1. Insufficient balance → ALWAYS shown first, before anything
//   2. Delivery gate (no subscription + no card) → warning, only
//      when the user can actually afford the transfer
//   3. PIN → transfer
//
// Held transfers show as "Pending" on the receipt with the
// notice repeated underneath.
// ===============================================================

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { useCountry } from "../context/CountryContext";
import PinPad from "../components/PinPad";
import { fetchDeliveryGate } from "../components/DeliveryGate";

const API_BASE = "https://alkebulanfkalertbackend.onrender.com/api";

// ---------------------------------------------------------------
// Wallet brand accents — one restrained color per wallet.
// ---------------------------------------------------------------
const BRAND = {
  opay:       { label: "ProBank Wallet", accent: "#0e9f6e", accentDark: "#0b7a55" },
  palmpay:    { label: "PalmPay",        accent: "#7c3aed", accentDark: "#5b21b6" },
  kuda:       { label: "Kuda",           accent: "#6d28d9", accentDark: "#4c1d95" },
  moniepoint: { label: "Moniepoint",     accent: "#1d4ed8", accentDark: "#1e3a8a" },
};

export default function TransferToBankPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isGhana, config } = useCountry();

  const brand = BRAND[location.state?.from] || BRAND.opay;

  // Country-locked configuration
  const country = isGhana
    ? {
        code: "GH",
        name: "Ghana",
        currencySymbol: "₵",
        digits: 10,
        title: "Transfer to Bank / Mobile Money",
        accountLabel: "Mobile Number / Account Number",
        bankPlaceholder: "Search Ghana banks & Mobile Money...",
      }
    : {
        code: "NG",
        name: "Nigeria",
        currencySymbol: "₦",
        digits: 10,
        title: "Transfer to Bank Account",
        accountLabel: "Account Number",
        bankPlaceholder: "Search Nigerian banks...",
      };

  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [resolvedName, setResolvedName] = useState("");
  const [resolvedBank, setResolvedBank] = useState(null);
  const [resolveFailed, setResolveFailed] = useState(false);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [banksLoading, setBanksLoading] = useState(false);
  const [recents, setRecents] = useState([]);
  const [step, setStep] = useState("enter"); // enter | confirm | receipt
  const [txnId, setTxnId] = useState(null);
  const [txnTime, setTxnTime] = useState(null);
  const [txnDelivered, setTxnDelivered] = useState(true);
  const [error, setError] = useState(null);
  const [accountLockError, setAccountLockError] = useState(null);
  const [user, setUser] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);

  // Delivery gate — checked on mount, enforced before the PIN pad
  const [canDeliver, setCanDeliver] = useState(true);
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    try {
      setUser(JSON.parse(localStorage.getItem("user") || "{}"));
    } catch {}
  }, []);

  // ---------------------------------------------------------------
  // Upfront gate check — no subscription + no card → warn first
  // ---------------------------------------------------------------
  useEffect(() => {
    fetchDeliveryGate().then(({ canDeliver }) => setCanDeliver(canDeliver));
  }, []);

  // ---------------------------------------------------------------
  // Bank list — locked to user's country
  // ---------------------------------------------------------------
  useEffect(() => {
    const fetchBanks = async () => {
      setBanksLoading(true);
      setSelectedBank("");
      setBanks([]);
      try {
        const res = await fetch(`${API_BASE}/bank/list?country=${country.code}`);
        const data = await res.json();
        setBanks(
          (Array.isArray(data) ? data : []).map((bank) => ({
            value: bank.code,
            label: bank.name,
          }))
        );
      } catch (err) {
        console.error("Error fetching banks:", err);
        setBanks([]);
      } finally {
        setBanksLoading(false);
      }
    };
    fetchBanks();
  }, [country.code]);

  // ---------------------------------------------------------------
  // Recent bank recipients (from the shared recipients API)
  // ---------------------------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    fetch(`${API_BASE}/bank/recipients?type=bank`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setRecents(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // ---------------------------------------------------------------
  // Live account-name verification
  // ---------------------------------------------------------------
  useEffect(() => {
    const resolveAccount = async () => {
      if (accountNumber.length === country.digits && selectedBank) {
        setLoading(true);
        setResolveFailed(false);
        try {
          const res = await fetch(`${API_BASE}/bank/resolve`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              account_number: accountNumber,
              bank_code: selectedBank,
              country: country.code,
            }),
          });
          const data = await res.json();
          const name = data?.account_name || data?.data?.account_name;

          if (name) {
            setResolvedName(name);
            setResolvedBank(data.bank_name || banks.find((b) => b.value === selectedBank)?.label || "");
            setResolveFailed(false);
          } else {
            setResolvedName("");
            setResolvedBank(null);
            setResolveFailed(true);
          }
        } catch (err) {
          setResolvedName("");
          setResolvedBank(null);
          setResolveFailed(true);
        } finally {
          setLoading(false);
        }
      } else {
        setResolvedName("");
        setResolvedBank(null);
        setResolveFailed(false);
      }
    };
    resolveAccount();
  }, [accountNumber, selectedBank, banks, country.code, country.digits]);

  const fmt = (n) =>
    Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const pickRecent = (r) => {
    setAccountNumber(r.accountNumber || "");
    if (r.bankCode) setSelectedBank(r.bankCode);
  };

  const canContinue = resolvedName && !loading && Number(amount) > 0;

  // Balance check — ALWAYS evaluated before any gate warning
  const hasInsufficientFunds = () => Number(amount) > Number(user?.balance || 0);

  const insufficientMessage = () =>
    `Insufficient balance. You are trying to send ${country.currencySymbol}${fmt(amount)} but your available balance is ${country.currencySymbol}${fmt(user?.balance)}. Please fund your wallet first.`;

  // Step 1 → Step 2: block early if the amount exceeds the balance
  const handleContinue = () => {
    if (hasInsufficientFunds()) {
      setError(insufficientMessage());
      return;
    }
    setStep("confirm");
  };

  // ---------------------------------------------------------------
  // Transfer with PIN — called by PinPad after the user enters
  // their 4-digit PIN. Returns { ok, data } so the pad can react
  // to PIN errors itself.
  // ---------------------------------------------------------------
  const submitTransfer = async (pin) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/bank/wallet/transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          account_number: accountNumber,
          bank_code: selectedBank,
          account_name: resolvedName,
          bank_name: resolvedBank,
          amount,
          country: country.code,
          pin,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setTxnId(data?.transaction?.transactionId || `TXN-${Date.now()}`);
        setTxnTime(new Date().toLocaleString());
        setTxnDelivered(data?.delivered !== false);

        try {
          const u = JSON.parse(localStorage.getItem("user") || "{}");
          u.balance = data?.transaction?.new_balance ?? u.balance;
          localStorage.setItem("user", JSON.stringify(u));
          setUser(u);
        } catch {}

        setStep("receipt");
      } else if (res.status === 403 && data.code === "ACCOUNT_LOCKED") {
        setAccountLockError(data.error || "Your account has been temporarily locked.");
        setStep("enter");
      } else if (!["PIN_INCORRECT", "PIN_LOCKED", "PIN_NOT_SET", "PIN_REQUIRED"].includes(data.code)) {
        setError(data.error || data.message || "Transfer failed. Please try again.");
        setStep("enter");
      }

      return { ok: res.ok, data };
    } catch (err) {
      setError("Transfer failed. Please check your connection and try again.");
      setStep("enter");
      return { ok: false, data: {} };
    }
  };

  const handleTransfer = () => {
    if (user?.accountLocked) {
      setAccountLockError(
        user?.accountLockReason ||
          "Your account has been temporarily locked. Please contact support."
      );
      return;
    }

    // Balance check ALWAYS comes first — before the gate warning
    if (hasInsufficientFunds()) {
      setError(insufficientMessage());
      return;
    }

    // Gate only when funds are sufficient
    if (!canDeliver) {
      setGateOpen(true);
      return;
    }

    // Open the PIN pad — the transfer fires from inside it
    setPinOpen(true);
  };

  // ---------------------------------------------------------------
  // Shared styles — clean banking UI
  // ---------------------------------------------------------------
  const S = {
    page: { minHeight: "100vh", background: "#f4f6f9", fontFamily: "'Inter', -apple-system, 'Segoe UI', sans-serif", color: "#0f172a" },
    header: { background: "#ffffff", borderBottom: "1px solid #e2e8f0", padding: "14px 20px", display: "flex", alignItems: "center", gap: 14, position: "sticky", top: 0, zIndex: 20 },
    backBtn: { background: "none", border: "none", fontSize: 20, color: "#0f172a", cursor: "pointer", padding: "2px 6px" },
    card: { background: "#ffffff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "20px", marginBottom: 16 },
    label: { display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 6, letterSpacing: "0.02em", textTransform: "uppercase" },
    input: { width: "100%", padding: "13px 14px", borderRadius: 10, border: "1px solid #cbd5e1", background: "#ffffff", color: "#0f172a", fontSize: 15, outline: "none", boxSizing: "border-box" },
    primaryBtn: { width: "100%", padding: "14px", borderRadius: 10, border: "none", background: brand.accent, color: "#ffffff", fontWeight: 700, fontSize: 15, cursor: "pointer" },
    primaryBtnDisabled: { width: "100%", padding: "14px", borderRadius: 10, border: "none", background: "#cbd5e1", color: "#64748b", fontWeight: 700, fontSize: 15, cursor: "not-allowed" },
    ghostBtn: { width: "100%", padding: "12px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#ffffff", color: "#64748b", fontWeight: 600, fontSize: 14, cursor: "pointer" },
    row: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "11px 0", borderTop: "1px solid #f1f5f9" },
    rowKey: { fontSize: 13, color: "#64748b" },
    rowVal: { fontSize: 13, fontWeight: 600, color: "#0f172a", textAlign: "right", maxWidth: "60%", wordBreak: "break-word" },
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "#ffffff",
      border: `1px solid ${state.isFocused ? brand.accent : "#cbd5e1"}`,
      borderRadius: 10,
      padding: "3px",
      boxShadow: "none",
      minHeight: 48,
    }),
    singleValue: (base) => ({ ...base, color: "#0f172a" }),
    input: (base) => ({ ...base, color: "#0f172a" }),
    menu: (base) => ({ ...base, backgroundColor: "#ffffff", borderRadius: 10, border: "1px solid #e2e8f0", zIndex: 30 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#f1f5f9" : "#ffffff",
      color: "#0f172a",
    }),
    placeholder: (base) => ({ ...base, color: "#94a3b8" }),
  };

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <button onClick={() => navigate(-1)} style={S.backBtn} aria-label="Back">←</button>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{country.title}</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{country.name} · {brand.label}</div>
        </div>
      </div>

      <div style={{ maxWidth: 480, margin: "0 auto", padding: "20px 16px 60px" }}>

        {/* Balance strip */}
        <div style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px" }}>
          <span style={{ fontSize: 13, color: "#64748b" }}>Available Balance</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: brand.accent }}>
            {country.currencySymbol}{fmt(user?.balance)}
          </span>
        </div>

        {/* STEP 1 — Enter details */}
        {step === "enter" && (
          <>
            <div style={S.card}>
              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>Recipient Bank</label>
                <Select
                  options={banks}
                  value={banks.find((o) => o.value === selectedBank) || null}
                  onChange={(option) => setSelectedBank(option?.value || "")}
                  placeholder={banksLoading ? "Loading banks..." : country.bankPlaceholder}
                  isSearchable
                  isLoading={banksLoading}
                  styles={selectStyles}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>{country.accountLabel}</label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, country.digits))}
                  maxLength={country.digits}
                  placeholder={`Enter ${country.digits}-digit number`}
                  inputMode="numeric"
                  style={S.input}
                />
                {loading && accountNumber.length === country.digits && (
                  <div style={{ fontSize: 12, color: brand.accent, marginTop: 6 }}>Verifying account…</div>
                )}
                {resolvedName && !loading && (
                  <div style={{
                    marginTop: 8, padding: "9px 12px", borderRadius: 8,
                    background: "#ecfdf5", border: "1px solid #a7f3d0",
                    fontSize: 13, fontWeight: 600, color: "#047857",
                  }}>
                    {resolvedName}{resolvedBank ? ` — ${resolvedBank}` : ""}
                  </div>
                )}
                {resolveFailed && !loading && (
                  <div style={{
                    marginTop: 8, padding: "9px 12px", borderRadius: 8,
                    background: "#fef2f2", border: "1px solid #fecaca",
                    fontSize: 13, color: "#b91c1c",
                  }}>
                    Could not verify this account. Please check the details.
                  </div>
                )}
              </div>

              <div>
                <label style={S.label}>Amount ({country.currencySymbol})</label>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                  placeholder="0.00"
                  inputMode="decimal"
                  style={{ ...S.input, fontSize: 20, fontWeight: 700 }}
                />
              </div>

              <button
                onClick={handleContinue}
                disabled={!canContinue}
                style={{ ...(canContinue ? S.primaryBtn : S.primaryBtnDisabled), marginTop: 18 }}
              >
                Continue
              </button>
            </div>

            {/* Recent recipients */}
            {recents.length > 0 && (
              <div style={S.card}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Recent Recipients</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 12 }}>Tap to fill the form automatically</div>
                {recents.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => pickRecent(r)}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", gap: 12,
                      padding: "11px 0", background: "none", border: "none",
                      borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
                      cursor: "pointer", textAlign: "left",
                    }}
                  >
                    <div style={{
                      width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                      background: "#f1f5f9", color: brand.accent,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 14, fontWeight: 700,
                    }}>
                      {(r.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{r.name}</div>
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>
                        {r.accountNumber}{r.bankName ? ` · ${r.bankName}` : ""}
                      </div>
                    </div>
                    <span style={{ color: "#cbd5e1", fontSize: 16 }}>›</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* STEP 2 — Confirm */}
        {step === "confirm" && (
          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", letterSpacing: "0.03em", marginBottom: 16 }}>
              CONFIRM TRANSFER
            </div>

            <div style={{ textAlign: "center", padding: "8px 0 22px" }}>
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>You are sending</div>
              <div style={{ fontSize: 34, fontWeight: 800, color: "#0f172a" }}>
                {country.currencySymbol}{fmt(amount)}
              </div>
            </div>

            {[
              ["Recipient", resolvedName],
              ["Bank", resolvedBank],
              [country.accountLabel, accountNumber],
              ["Country", country.name],
            ].map(([k, v]) => (
              <div key={k} style={S.row}>
                <span style={S.rowKey}>{k}</span>
                <span style={S.rowVal}>{v}</span>
              </div>
            ))}

            <button onClick={handleTransfer} style={{ ...S.primaryBtn, marginTop: 20 }}>
              Confirm Transfer
            </button>
            <button onClick={() => setStep("enter")} style={{ ...S.ghostBtn, marginTop: 10 }}>
              Back
            </button>
          </div>
        )}

        {/* STEP 3 — Receipt */}
        {step === "receipt" && (
          <div style={S.card}>
            <div style={{ textAlign: "center", padding: "8px 0 20px", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{
                width: 52, height: 52, margin: "0 auto 12px", borderRadius: "50%",
                background: txnDelivered ? "#ecfdf5" : "#fffbeb",
                border: txnDelivered ? "1px solid #a7f3d0" : "1px solid #fde68a",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 22, color: txnDelivered ? "#047857" : "#b45309", fontWeight: 800,
              }}>
                {txnDelivered ? "✓" : "⏳"}
              </div>
              <div style={{ fontSize: 17, fontWeight: 700 }}>
                {txnDelivered ? "Transfer Successful" : "Transfer Recorded"}
              </div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{txnTime}</div>
              <div style={{ fontSize: 30, fontWeight: 800, marginTop: 14, color: brand.accent }}>
                {country.currencySymbol}{fmt(amount)}
              </div>
            </div>

            <div style={{ paddingTop: 8 }}>
              {[
                ["Recipient", resolvedName],
                ["Bank", resolvedBank],
                ["Account Number", accountNumber],
                ["Transaction Reference", txnId],
                ["Status", txnDelivered ? "Successful" : "Pending — not yet with recipient"],
              ].map(([k, v]) => (
                <div key={k} style={S.row}>
                  <span style={S.rowKey}>{k}</span>
                  <span style={{
                    ...S.rowVal,
                    ...(k === "Status" && !txnDelivered ? { color: "#b45309" } : {}),
                  }}>{v}</span>
                </div>
              ))}
            </div>

            {!txnDelivered && (
              <div style={{
                marginTop: 16, padding: "12px 14px", borderRadius: 10,
                background: "#fffbeb", border: "1px solid #fde68a",
                fontSize: 12.5, color: "#92400e", lineHeight: 1.6,
              }}>
                This transaction is recorded on your account, but it will not reflect
                in the recipient's account until you subscribe to a plan or activate
                a ProBank card.
              </div>
            )}

            <button
              onClick={() => {
                setStep("enter");
                setAccountNumber("");
                setResolvedName("");
                setSelectedBank("");
                setAmount("");
                setTxnId(null);
                setTxnTime(null);
                setTxnDelivered(true);
              }}
              style={{ ...S.primaryBtn, marginTop: 20 }}
            >
              Done
            </button>
            <button onClick={() => navigate("/dashboard")} style={{ ...S.ghostBtn, marginTop: 10 }}>
              Back to Dashboard
            </button>
          </div>
        )}
      </div>

      {/* Pre-transfer delivery warning — light theme to match page */}
      {gateOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 110 }}>
          <div style={{ width: "100%", maxWidth: 440, background: "#ffffff", borderRadius: "18px 18px 0 0", padding: "26px 24px 34px" }}>
            <div style={{ width: 40, height: 4, background: "#e2e8f0", borderRadius: 4, margin: "0 auto 20px" }} />
            <div style={{ textAlign: "center" }}>
              <div style={{
                width: 48, height: 48, margin: "0 auto 14px", borderRadius: "50%",
                background: "#fffbeb", border: "1px solid #fde68a",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, fontWeight: 800, color: "#b45309",
              }}>
                !
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>Transaction Notice</div>
              <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.65, marginBottom: 18 }}>
                This transaction will go through on your side, but it will{" "}
                <b style={{ color: "#0f172a" }}>not reflect in the recipient's account</b>{" "}
                until you subscribe to a plan or activate a ProBank card.
              </div>

              <button
                onClick={() => { setGateOpen(false); setPinOpen(true); }}
                style={{ ...S.primaryBtn, marginBottom: 10 }}
              >
                Continue Anyway
              </button>

              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <button
                  onClick={() => navigate("/subscription")}
                  style={{ ...S.ghostBtn, flex: 1, fontSize: 13 }}
                >
                  Subscribe to a Plan
                </button>
                <button
                  onClick={() => navigate("/cards")}
                  style={{ ...S.ghostBtn, flex: 1, fontSize: 13 }}
                >
                  Get a ProBank Card
                </button>
              </div>

              <button
                onClick={() => setGateOpen(false)}
                style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "6px" }}
              >
                Cancel Transaction
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction PIN pad */}
      <PinPad
        open={pinOpen}
        onClose={() => setPinOpen(false)}
        onSuccess={submitTransfer}
        title="Authorize Transfer"
        subtitle={`${country.currencySymbol}${fmt(amount)} to ${resolvedName || "recipient"}`}
        brand={{ accent: brand.accent, accentText: "#ffffff" }}
      />

      {/* Account locked — bottom sheet */}
      {accountLockError && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 100 }}>
          <div style={{ width: "100%", maxWidth: 440, background: "#ffffff", borderRadius: "18px 18px 0 0", padding: "26px 24px 34px" }}>
            <div style={{ width: 40, height: 4, background: "#e2e8f0", borderRadius: 4, margin: "0 auto 20px" }} />
            <div style={{ textAlign: "center" }}>
              <div style={{
                width: 48, height: 48, margin: "0 auto 14px", borderRadius: "50%",
                background: "#fef2f2", border: "1px solid #fecaca",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="#b91c1c" viewBox="0 0 24 24" style={{ width: 22, height: 22 }}>
                  <path fillRule="evenodd" d="M12 1a4 4 0 014 4v3h1a3 3 0 013 3v9a3 3 0 01-3 3H7a3 3 0 01-3-3v-9a3 3 0 013-3h1V5a4 4 0 014-4zm0 2a2 2 0 00-2 2v3h4V5a2 2 0 00-2-2z" clipRule="evenodd" />
                </svg>
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Account Locked</div>
              <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6, marginBottom: 8 }}>
                You cannot perform this transfer at the moment.
              </div>
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#b91c1c", marginBottom: 18 }}>
                {accountLockError}
              </div>
              <button onClick={() => setAccountLockError(null)} style={S.primaryBtn}>
                I Understand
              </button>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 12 }}>Contact support if you need help</div>
            </div>
          </div>
        </div>
      )}

      {/* General error / insufficient balance — bottom sheet */}
      {error && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 100 }}>
          <div style={{ width: "100%", maxWidth: 440, background: "#ffffff", borderRadius: "18px 18px 0 0", padding: "26px 24px 34px" }}>
            <div style={{ width: 40, height: 4, background: "#e2e8f0", borderRadius: 4, margin: "0 auto 20px" }} />
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Transaction Cannot Continue</div>
              <div style={{ fontSize: 13, color: "#b91c1c", marginBottom: 18 }}>{error}</div>
              <button onClick={() => setError(null)} style={{ ...S.primaryBtn, background: "#b91c1c" }}>
                Okay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
